import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Robot,
  Plus,
  Trash,
  ChatCircle,
  CheckSquare,
  ListBullets,
  Star,
  CircleNotch,
  Image as ImageIcon,
  X,
  CaretLeft,
  CheckCircle,
  Power,
} from "@phosphor-icons/react";

const NODE_TYPES_META = {
  message: { icon: ChatCircle, label: "Message", color: "#10B981" },
  buttons: { icon: CheckSquare, label: "Buttons", color: "#3B82F6" },
  list: { icon: ListBullets, label: "List", color: "#8B5CF6" },
};

function uid(prefix = "n") {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

// ---------- Custom node renderers ----------
function NodeShell({ id, data, selected, isStart, color, icon: Icon, children }) {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border-2 min-w-[220px] max-w-[280px] ${
        selected ? "border-[#25D366]" : "border-[#E5E7EB]"
      }`}
    >
      <Handle type="target" position={Position.Left} style={{ background: "#9CA3AF" }} />
      <div
        className="px-3 py-1.5 rounded-t-md flex items-center justify-between gap-2 text-white text-[11px] font-semibold"
        style={{ background: color }}
      >
        <span className="flex items-center gap-1.5">
          <Icon size={12} weight="fill" />
          {NODE_TYPES_META[data.type]?.label || data.type}
        </span>
        {isStart && (
          <span className="flex items-center gap-1 text-[10px] bg-white/25 px-1.5 py-0.5 rounded">
            <Star size={10} weight="fill" /> START
          </span>
        )}
      </div>
      <div className="p-2 text-xs text-[#111827]">{children}</div>
    </div>
  );
}

function MessageNode({ id, data, selected }) {
  return (
    <NodeShell id={id} data={{ ...data, type: "message" }} selected={selected} isStart={data.is_start} color="#10B981" icon={ChatCircle}>
      {data.media_id && (
        <div className="mb-1.5 flex items-center gap-1 text-[10px] text-[#6B7280]">
          <ImageIcon size={11} /> {data.media_kind || "media"}
        </div>
      )}
      <div className="whitespace-pre-wrap line-clamp-4 leading-snug">{data.text || <em className="text-[#9CA3AF]">No text</em>}</div>
      <Handle type="source" position={Position.Right} style={{ background: "#10B981" }} />
    </NodeShell>
  );
}

function ButtonsNode({ id, data, selected }) {
  const buttons = (data.buttons || []).slice(0, 3);
  return (
    <NodeShell id={id} data={{ ...data, type: "buttons" }} selected={selected} isStart={data.is_start} color="#3B82F6" icon={CheckSquare}>
      <div className="whitespace-pre-wrap line-clamp-3 leading-snug mb-2">{data.text || <em className="text-[#9CA3AF]">No text</em>}</div>
      <div className="space-y-1">
        {buttons.length === 0 ? (
          <div className="text-[10px] text-[#9CA3AF] italic">Add buttons →</div>
        ) : (
          buttons.map((b, i) => (
            <div key={b.id || i} className="relative bg-[#F3F4F6] rounded px-2 py-1 text-[11px] flex items-center justify-between">
              <span className="truncate">{b.text || "Button"}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={b.id}
                style={{
                  background: "#3B82F6", width: 10, height: 10,
                  right: -16, top: "50%", transform: "translateY(-50%)",
                }}
              />
            </div>
          ))
        )}
      </div>
    </NodeShell>
  );
}

function ListNode({ id, data, selected }) {
  const items = (data.list_items || []).slice(0, 10);
  return (
    <NodeShell id={id} data={{ ...data, type: "list" }} selected={selected} isStart={data.is_start} color="#8B5CF6" icon={ListBullets}>
      <div className="whitespace-pre-wrap line-clamp-3 leading-snug mb-2">{data.text || <em className="text-[#9CA3AF]">No text</em>}</div>
      <div className="space-y-1">
        {items.length === 0 ? (
          <div className="text-[10px] text-[#9CA3AF] italic">Add list items →</div>
        ) : (
          items.map((it, i) => (
            <div key={it.id || i} className="relative bg-[#F3F4F6] rounded px-2 py-1 text-[11px] flex items-center justify-between">
              <span className="truncate">{it.text || "Item"}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={it.id}
                style={{
                  background: "#8B5CF6", width: 10, height: 10,
                  right: -16, top: "50%", transform: "translateY(-50%)",
                }}
              />
            </div>
          ))
        )}
      </div>
    </NodeShell>
  );
}

const nodeTypes = {
  message: MessageNode,
  buttons: ButtonsNode,
  list: ListNode,
};

// ---------- Right panel node editor ----------
function NodeEditor({ node, onChange, onDelete, onSetStart, onClose }) {
  if (!node) return null;
  const data = node.data || {};
  const setData = (patch) => onChange({ ...node, data: { ...data, ...patch } });

  const updateButton = (idx, patch) => {
    const arr = [...(data.buttons || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setData({ buttons: arr });
  };
  const addButton = () => {
    const arr = [...(data.buttons || [])];
    if (arr.length >= 3) return toast.error("Max 3 buttons allowed");
    arr.push({ id: uid("btn"), text: "Option" });
    setData({ buttons: arr });
  };
  const delButton = (idx) => {
    const arr = [...(data.buttons || [])];
    arr.splice(idx, 1);
    setData({ buttons: arr });
  };

  const updateItem = (idx, patch) => {
    const arr = [...(data.list_items || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setData({ list_items: arr });
  };
  const addItem = () => {
    const arr = [...(data.list_items || [])];
    if (arr.length >= 10) return toast.error("Max 10 items allowed");
    arr.push({ id: uid("li"), text: "Item", description: "" });
    setData({ list_items: arr });
  };
  const delItem = (idx) => {
    const arr = [...(data.list_items || [])];
    arr.splice(idx, 1);
    setData({ list_items: arr });
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("wh_token");
      const res = await fetch(`${API_BASE}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        throw new Error(typeof d === "string" ? d : d?.message || "Upload failed");
      }
      const d = await res.json();
      const kind = file.type.startsWith("image/") ? "image"
        : file.type.startsWith("video/") ? "video"
        : file.type.startsWith("audio/") ? "audio"
        : "document";
      setData({ media_id: d.media_id, media_kind: kind, media_filename: file.name });
      toast.success("Media uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="node-editor">
      <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-[var(--muted)]">Edit Node</div>
          <div className="text-sm font-semibold heading capitalize">{NODE_TYPES_META[node.type]?.label || node.type}</div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X size={14} />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin px-4 py-3 space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Message text</Label>
          <textarea
            value={data.text || ""}
            onChange={(e) => setData({ text: e.target.value })}
            placeholder="Type the message body…"
            rows={4}
            className="w-full text-xs px-2 py-1.5 rounded-md border border-[var(--border)] bg-[var(--bg)] resize-none"
            data-testid="node-text"
          />
          <div className="text-[10px] text-[var(--muted)]">Supports *bold* and _italic_</div>
        </div>

        {/* Media (message + button header) */}
        {(node.type === "message" || node.type === "buttons") && (
          <div className="space-y-1.5">
            <Label className="text-xs">Media (optional)</Label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*,video/*,application/pdf"
                id={`media-${node.id}`}
                className="hidden"
                onChange={(e) => uploadMedia(e.target.files?.[0])}
                data-testid="node-media-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs h-8"
                onClick={() => document.getElementById(`media-${node.id}`)?.click()}
              >
                <ImageIcon size={12} className="mr-1.5" />
                {data.media_id ? "Replace" : "Upload"}
              </Button>
              {data.media_id && (
                <>
                  <span className="text-[10px] text-[var(--muted)] truncate flex-1">
                    {data.media_filename || data.media_kind}
                  </span>
                  <Button
                    type="button" variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setData({ media_id: null, media_kind: null, media_filename: null })}
                  >
                    <X size={12} />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Buttons */}
        {node.type === "buttons" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Buttons ({(data.buttons || []).length}/3)</Label>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                onClick={addButton} data-testid="add-button">
                <Plus size={12} className="mr-1" /> Add
              </Button>
            </div>
            {(data.buttons || []).map((b, i) => (
              <div key={b.id} className="flex items-center gap-1.5">
                <Input
                  value={b.text}
                  onChange={(e) => updateButton(i, { text: e.target.value })}
                  maxLength={20}
                  className="h-8 text-xs"
                  placeholder="Button label (max 20 chars)"
                  data-testid={`button-text-${i}`}
                />
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                  onClick={() => delButton(i)}>
                  <Trash size={12} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* List */}
        {node.type === "list" && (
          <div className="space-y-2">
            <div className="space-y-1.5">
              <Label className="text-xs">List button label</Label>
              <Input
                value={data.list_title || ""}
                onChange={(e) => setData({ list_title: e.target.value })}
                placeholder="Select One"
                maxLength={20}
                className="h-8 text-xs"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Items ({(data.list_items || []).length}/10)</Label>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                onClick={addItem} data-testid="add-list-item">
                <Plus size={12} className="mr-1" /> Add
              </Button>
            </div>
            {(data.list_items || []).map((it, i) => (
              <div key={it.id} className="space-y-1 border border-[var(--border)] rounded p-1.5">
                <div className="flex items-center gap-1.5">
                  <Input
                    value={it.text}
                    onChange={(e) => updateItem(i, { text: e.target.value })}
                    maxLength={24}
                    className="h-7 text-xs"
                    placeholder="Title (max 24)"
                  />
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => delItem(i)}>
                    <Trash size={12} />
                  </Button>
                </div>
                <Input
                  value={it.description || ""}
                  onChange={(e) => updateItem(i, { description: e.target.value })}
                  maxLength={72}
                  className="h-7 text-xs"
                  placeholder="Description (optional, max 72)"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-[var(--border)] flex items-center gap-2">
        <Button
          type="button"
          variant={node.data.is_start ? "default" : "outline"}
          size="sm"
          className={`text-xs gap-1.5 flex-1 ${node.data.is_start ? "bg-[#F59E0B] text-white hover:bg-[#D97706]" : ""}`}
          onClick={onSetStart}
          data-testid="set-start-btn"
        >
          <Star size={12} weight={node.data.is_start ? "fill" : "regular"} />
          {node.data.is_start ? "Start node" : "Set as start"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDelete}
          className="text-xs text-[#991B1B] border-[#FCA5A5] hover:bg-[#FEE2E2]"
          data-testid="delete-node-btn"
        >
          <Trash size={12} className="mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}

// ---------- Bot list view ----------
function BotList({ items, onCreate, onOpen, onDelete, onActivate, onDeactivate, refreshing, onRefresh }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="heading text-lg font-semibold">Chat Bots</div>
          <div className="text-xs text-[var(--muted)]">
            Visual flow builder. One bot can be active at a time. Active bot replies to every new driver message.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? <CircleNotch size={14} className="animate-spin" /> : "Refresh"}
          </Button>
          <Button size="sm" onClick={onCreate} className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5"
            data-testid="bot-new-btn">
            <Plus size={14} weight="bold" /> New Bot
          </Button>
        </div>
      </div>

      {items.some((b) => b.is_active) && (
        <Button
          variant="outline" size="sm" onClick={onDeactivate}
          className="gap-1.5 text-[#92400E] border-[#FBBF24] hover:bg-[#FEF3C7]"
          data-testid="deactivate-all-btn"
        >
          <Power size={14} /> Deactivate active bot
        </Button>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] rounded-lg p-10 text-center text-sm text-[var(--muted)]">
          <Robot size={32} className="mx-auto mb-3 opacity-40" />
          No bots yet. Click <b>New Bot</b> to design your first flow.
        </div>
      ) : (
        <ul className="space-y-2 max-h-[55vh] overflow-y-auto scroll-thin pr-1">
          {items.map((b) => (
            <li
              key={b.id}
              className="border border-[var(--border)] rounded-lg p-3 hover:bg-[var(--bg)] cursor-pointer"
              onClick={() => onOpen(b.id)}
              data-testid={`bot-row-${b.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{b.name}</span>
                    {b.is_active && (
                      <Badge className="bg-[#D1FAE5] text-[#065F46] hover:bg-[#D1FAE5] text-[10px]">
                        <CheckCircle size={10} weight="fill" className="mr-1" /> ACTIVE
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--muted)] mt-0.5">
                    {b.nodes?.length || 0} nodes · {b.edges?.length || 0} edges
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost" size="icon" className="h-7 w-7 text-[#991B1B]"
                  onClick={(e) => { e.stopPropagation(); onDelete(b.id, b.name); }}
                  data-testid={`bot-delete-${b.id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Bot editor (canvas) ----------
function BotEditor({ botId, onBack, onSaved }) {
  const [bot, setBot] = useState(null);
  const [name, setName] = useState("");
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [startNodeId, setStartNodeId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (!botId) return;
    api.get(`/bots/${botId}`).then((r) => {
      const b = r.data;
      setBot(b);
      setName(b.name);
      setStartNodeId(b.start_node_id);
      setNodes((b.nodes || []).map((n) => ({
        id: n.id, type: n.type, position: n.position || { x: 100, y: 100 },
        data: { ...n.data, type: n.type, is_start: n.id === b.start_node_id },
      })));
      setEdges((b.edges || []).map((e) => ({
        id: e.id, source: e.source, sourceHandle: e.sourceHandle, target: e.target,
      })));
    }).catch(() => toast.error("Failed to load bot"));
  }, [botId]);

  // Sync is_start flag in node.data when startNodeId changes
  useEffect(() => {
    setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, is_start: n.id === startNodeId } })));
  }, [startNodeId]);

  const onNodesChange = useCallback((changes) => setNodes((n) => applyNodeChanges(changes, n)), []);
  const onEdgesChange = useCallback((changes) => setEdges((e) => applyEdgeChanges(changes, e)), []);
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, id: uid("e") }, eds)),
    [],
  );

  const addNode = (type) => {
    const id = uid(type[0]);
    const baseData =
      type === "buttons" ? { text: "Choose:", buttons: [{ id: uid("btn"), text: "Option 1" }] } :
      type === "list" ? { text: "Pick one:", list_title: "Select One", list_items: [{ id: uid("li"), text: "Item 1" }] } :
      { text: "Hello!" };
    const newNode = {
      id, type, position: { x: 250 + Math.random() * 200, y: 100 + nodes.length * 30 },
      data: { ...baseData, type },
    };
    setNodes((n) => [...n, newNode]);
    setSelectedId(id);
    if (!startNodeId) setStartNodeId(id);
  };

  const updateSelected = (updated) => {
    setNodes((nds) => nds.map((n) => (n.id === updated.id ? { ...n, data: updated.data } : n)));
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this node?")) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedId && e.target !== selectedId));
    if (startNodeId === selectedId) setStartNodeId(null);
    setSelectedId(null);
  };

  const setStart = () => {
    if (!selectedId) return;
    setStartNodeId(selectedId);
    toast.success("Start node updated");
  };

  const save = async (alsoActivate = false) => {
    if (!name.trim()) return toast.error("Bot name daaliye");
    if (nodes.length === 0) return toast.error("Add at least one node");
    if (!startNodeId) return toast.error("Star (⭐) ek node ko start node banane ke liye");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        nodes: nodes.map((n) => ({
          id: n.id, type: n.type, position: n.position,
          data: Object.fromEntries(Object.entries(n.data).filter(([k]) => k !== "is_start" && k !== "type")),
        })),
        edges: edges.map((e) => ({
          id: e.id, source: e.source, sourceHandle: e.sourceHandle || null, target: e.target,
        })),
        start_node_id: startNodeId,
      };
      await api.patch(`/bots/${botId}`, payload);
      toast.success("Bot saved");
      if (alsoActivate) {
        setActivating(true);
        await api.post(`/bots/${botId}/activate`);
        toast.success("Bot activated — driver replies will now go through this flow");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
      setActivating(false);
    }
  };

  const selectedNode = nodes.find((n) => n.id === selectedId);

  return (
    <div className="h-[78vh] flex flex-col">
      {/* Top bar */}
      <div className="flex items-center gap-2 mb-2">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <CaretLeft size={18} />
        </Button>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Bot name"
          className="h-9 text-sm max-w-xs font-medium"
          data-testid="bot-name-input"
        />
        <div className="flex items-center gap-1.5 ml-2">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => addNode("message")} data-testid="add-message-node">
            <ChatCircle size={12} weight="fill" className="text-[#10B981]" /> + Message
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => addNode("buttons")} data-testid="add-buttons-node">
            <CheckSquare size={12} weight="fill" className="text-[#3B82F6]" /> + Buttons
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => addNode("list")} data-testid="add-list-node">
            <ListBullets size={12} weight="fill" className="text-[#8B5CF6]" /> + List
          </Button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {bot?.is_active && (
            <Badge className="bg-[#D1FAE5] text-[#065F46] hover:bg-[#D1FAE5] text-[10px]">
              <CheckCircle size={10} weight="fill" className="mr-1" /> ACTIVE
            </Badge>
          )}
          <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving} data-testid="bot-save-btn">
            {saving ? <CircleNotch size={14} className="animate-spin" /> : "Save"}
          </Button>
          <Button
            size="sm"
            onClick={() => save(true)}
            disabled={saving || activating}
            className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5"
            data-testid="bot-save-activate-btn"
          >
            <Power size={12} weight="fill" />
            Save & Activate
          </Button>
        </div>
      </div>

      {/* Canvas + side panel */}
      <div className="flex-1 grid grid-cols-[1fr_320px] gap-3 min-h-0">
        <div className="border border-[var(--border)] rounded-lg overflow-hidden bg-[#FAFAFA]">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onPaneClick={() => setSelectedId(null)}
            nodeTypes={nodeTypes}
            fitView
          >
            <Background gap={20} color="#E5E7EB" />
            <Controls />
            <MiniMap zoomable pannable />
          </ReactFlow>
        </div>
        <div className="border border-[var(--border)] rounded-lg bg-[var(--surface)] overflow-hidden">
          {selectedNode ? (
            <NodeEditor
              node={selectedNode}
              onChange={updateSelected}
              onDelete={deleteSelected}
              onSetStart={setStart}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-6 text-center text-xs text-[var(--muted)]">
              <Robot size={28} className="opacity-40 mb-2" />
              <div className="font-medium mb-1">Select a node to edit</div>
              <div>Click any node on the canvas. Or add a new node from top toolbar.</div>
              <div className="mt-3 text-[10px]">⭐ marks the start node — first message driver receives.</div>
              <div className="mt-1 text-[10px]">Drag from a button/item handle to another node to connect.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Top-level dialog ----------
export default function BotsDialog({ open, onOpenChange }) {
  const [view, setView] = useState("list"); // list | editor
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/bots");
      setItems(r.data.items || []);
    } catch (_) {}
    finally { setRefreshing(false); }
  };

  useEffect(() => {
    if (!open) return;
    setView("list");
    refresh();
  }, [open]);

  const createBot = async () => {
    const name = window.prompt("Bot name?");
    if (!name?.trim()) return;
    try {
      const r = await api.post("/bots", { name: name.trim(), nodes: [], edges: [] });
      setActiveId(r.data.id);
      setView("editor");
      refresh();
    } catch (e) {
      toast.error("Failed to create bot");
    }
  };

  const deleteBot = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Sessions will be cleared.`)) return;
    try {
      await api.delete(`/bots/${id}`);
      toast.success("Bot deleted");
      refresh();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  const deactivateAll = async () => {
    try {
      await api.post("/bots/deactivate");
      toast.success("All bots deactivated");
      refresh();
    } catch (_) {
      toast.error("Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl w-[95vw] max-h-[95vh] overflow-y-auto" data-testid="bots-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Robot size={20} weight="fill" className="text-[#25D366]" />
            Chat Bot Builder
          </DialogTitle>
          <DialogDescription>
            Design WhatsApp chatbot flows visually. Drag nodes, connect edges, save & activate.
          </DialogDescription>
        </DialogHeader>
        {view === "list" ? (
          <BotList
            items={items}
            refreshing={refreshing}
            onRefresh={refresh}
            onCreate={createBot}
            onOpen={(id) => { setActiveId(id); setView("editor"); }}
            onDelete={deleteBot}
            onDeactivate={deactivateAll}
          />
        ) : (
          <ReactFlowProvider>
            <BotEditor
              botId={activeId}
              onBack={() => { setView("list"); refresh(); }}
              onSaved={refresh}
            />
          </ReactFlowProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}
